import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Camera, Ruler, StickyNote, ListChecks, Sparkles, Trash2, Loader2,
  FileText, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, Wrench, ShoppingCart, Copy,
  ClipboardCheck, Download, Send, CalendarClock, MailCheck, HardHat, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";
import { StudioLayout } from "./StudioLayout";
import { OfflineBanner } from "@/components/OfflineBanner";
import { queueChild, isOffline } from "@/lib/offline";
import { savePhotoOffline } from "@/lib/offlinePhotos";
import { TRADE_FORMS } from "./tradeForms";
import { PhotoUploader } from "@/components/PhotoUploader";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { PlateReader } from "@/components/PlateReader";
import { DateField } from "@/components/DateField";
import { mediaUrl } from "@/lib/photos";
import { StatusBadge, PageSkeleton } from "@/components/studio/StudioUI";
import {
  studio, apiError, beDate, inputCls, btnPrimary, btnGhost,
  STATUS_OPTIONS, STATUS_CLS, PHOTO_CATEGORIES, eur,
} from "@/lib/studioApi";

const Section = ({ title, icon: Icon, children, testId, action = null }) => (
  <div data-testid={testId} className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#3A5CC8]" /> {title}
      </h2>
      {action}
    </div>
    {children}
  </div>
);

export default function StudioDossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [note, setNote] = useState("");
  const [measure, setMeasure] = useState({ label: "", value: "", unit: "" });
  const [material, setMaterial] = useState({ label: "", quantity: "1", unit: "", supplier: "" });
  const [photoCategory, setPhotoCategory] = useState("avant");
  const [openForm, setOpenForm] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [reportBusy, setReportBusy] = useState("");
  const [visitPlan, setVisitPlan] = useState(null);
  const [visitBusy, setVisitBusy] = useState(false);
  const [interventions, setInterventions] = useState([]);

  const load = useCallback(() => {
    studio.get(`/dossiers/${id}`).then(({ data }) => setDossier(data)).catch(() => toast.error("Dossier introuvable."));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    studio.get(`/interventions?dossier_id=${id}`).then(({ data }) => setInterventions(data)).catch(() => {});
  }, [id]);

  const createIntervention = async (quoteId = "") => {
    try {
      const { data } = await studio.post("/interventions", { dossier_id: id, quote_id: quoteId });
      toast.success(`Intervention ${data.reference} créée.`);
      navigate(`/studio/interventions/${data.id}`);
    } catch (e) {
      toast.error(apiError(e, "Création impossible."));
    }
  };

  const patch = async (update) => {
    try {
      const { data } = await studio.put(`/dossiers/${id}`, update);
      setDossier((d) => ({ ...d, ...data }));
    } catch (e) {
      toast.error(apiError(e, "Enregistrement impossible."));
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      if (isOffline()) throw new Error("offline");
      await studio.post(`/dossiers/${id}/notes`, { text: note });
      setNote("");
      load();
    } catch (e) {
      if (!e.response) {
        queueChild(id, "notes", { text: note });
        setNote("");
        toast.success("Note enregistrée hors ligne.");
      } else { toast.error(apiError(e, "Ajout impossible.")); }
    }
  };

  const addMeasure = async () => {
    if (!measure.label.trim()) return;
    try {
      if (isOffline()) throw new Error("offline");
      await studio.post(`/dossiers/${id}/measures`, measure);
      setMeasure({ label: "", value: "", unit: "" });
      load();
    } catch (e) {
      if (!e.response) {
        queueChild(id, "measures", measure);
        setMeasure({ label: "", value: "", unit: "" });
        toast.success("Mesure enregistrée hors ligne.");
      } else { toast.error(apiError(e, "Ajout impossible.")); }
    }
  };

  const addMaterial = async () => {
    if (!material.label.trim()) return;
    try {
      if (isOffline()) throw new Error("offline");
      await studio.post(`/dossiers/${id}/materials`, material);
      setMaterial({ label: "", quantity: "1", unit: "", supplier: "" });
      load();
    } catch (e) {
      if (!e.response) {
        queueChild(id, "materials", material);
        setMaterial({ label: "", quantity: "1", unit: "", supplier: "" });
        toast.success("Matériel enregistré hors ligne.");
      } else { toast.error(apiError(e, "Ajout impossible.")); }
    }
  };

  const toggleMaterial = async (m) => {
    await studio.put(`/dossiers/${id}/materials/${m.id}`, { ...m, bought: !m.bought });
    load();
  };

  const attachPhotos = async (ids) => {
    const known = new Set((dossier.photos || []).map((p) => p.media_id));
    for (const mid of ids) {
      if (known.has(mid)) continue;
      await studio.post(`/dossiers/${id}/photos`, { media_id: mid, category: photoCategory });
    }
    load();
  };

  const keepPhotoOffline = async (file) => {
    await savePhotoOffline(id, file, photoCategory);
  };

  const removeItem = async (kind, itemId) => {
    try {
      await studio.delete(`/dossiers/${id}/${kind}/${itemId}`);
      load();
    } catch (e) { toast.error("Suppression impossible."); }
  };

  const removeQuote = async (q) => {
    if (!window.confirm(q.status === "draft" ? `Supprimer le brouillon ${q.number} ?` : `Supprimer le devis ${q.number} ?`)) return;
    try { await studio.delete(`/quotes/${q.id}`); toast.success(`${q.number} supprimé.`); load(); }
    catch (e) { toast.error(apiError(e, "Suppression impossible.")); }
  };

  const generateChecklist = async () => {
    try {
      await studio.post(`/dossiers/${id}/checklist`);
      load();
      toast.success("Checklist métier générée.");
    } catch (e) { toast.error("Génération impossible."); }
  };

  const toggleCheck = async (item) => {
    await studio.put(`/dossiers/${id}/checklist/${item.id}`, { done: !item.done });
    load();
  };

  const prepare = async () => {
    setPreparing(true);
    try {
      const { data } = await studio.post(`/dossiers/${id}/prepare-quote`, {}, { timeout: 300000 });
      if (!data.lines.length) {
        toast.error("L'assistant n'a pas trouvé assez d'informations. Ajoutez des notes ou une dictée.");
        return;
      }
      const quote = await studio.post("/quotes", {
        dossier_id: id,
        lines: data.lines.map(({ description, quantity, unit, unit_price, vat_percent }) => ({ description, quantity, unit, unit_price, vat_percent })),
        conditions: data.conditions,
        remarks: data.remarks,
      });
      sessionStorage.setItem(`studio_warnings_${quote.data.id}`, JSON.stringify(data.warnings || []));
      toast.success("Devis préparé par l'assistant.");
      navigate(`/studio/devis/${quote.data.id}`);
    } catch (e) {
      toast.error(apiError(e, "Préparation impossible. Réessayez."));
    } finally {
      setPreparing(false);
    }
  };

  const planVisit = async () => {
    const payload = {
      date: visitPlan?.date ?? dossier.visit_date ?? "",
      time_start: visitPlan?.time_start ?? dossier.visit_time_start ?? "",
      time_end: visitPlan?.time_end ?? dossier.visit_time_end ?? "",
      free_quote: visitPlan?.free_quote ?? dossier.visit_free_quote ?? false,
      reminder: visitPlan?.reminder ?? dossier.visit_reminder ?? true,
      note: visitPlan?.note ?? dossier.visit_note ?? "",
    };
    if (!payload.date) {
      toast.error("Choisissez la date de la visite.");
      return;
    }
    setVisitBusy(true);
    try {
      const { data } = await studio.post(`/dossiers/${id}/schedule-visit`, payload);
      toast.success(`Planification envoyée à ${data.client_email}.`);
      setVisitPlan(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Envoi impossible."));
    } finally {
      setVisitBusy(false);
    }
  };

  const downloadReport = async () => {
    setReportBusy("pdf");
    try {
      const res = await studio.get(`/dossiers/${id}/report/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport-visite-${dossier.reference}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e, "Génération du rapport impossible."));
    } finally {
      setReportBusy("");
    }
  };

  const sendReport = async () => {
    setReportBusy("send");
    try {
      await studio.post(`/dossiers/${id}/report/send`);
      toast.success("Rapport de visite envoyé au client.");
      load();
    } catch (e) {
      toast.error(apiError(e, "Envoi impossible."));
    } finally {
      setReportBusy("");
    }
  };

  if (!dossier) {
    return <StudioLayout title="Dossier"><PageSkeleton /></StudioLayout>;
  }

  const forms = TRADE_FORMS[dossier.trade] || TRADE_FORMS.autre;
  const structured = dossier.structured || {};

  return (
    <StudioLayout title={dossier.title} subtitle={`${dossier.reference} · ${dossier.client_name}${dossier.site_city ? ` · ${dossier.site_city}` : ""}`}>
      <button type="button" data-testid="dossier-back" onClick={() => navigate("/studio/dossiers")} className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#5C6A87] hover:text-[#0F2645] transition-colors">
        <ArrowLeft className="w-4 h-4" /> Tous les dossiers
      </button>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <select data-testid="dossier-status" value={dossier.status} onChange={(e) => patch({ status: e.target.value })} className={`${inputCls} max-w-[15rem] py-2.5`}>
          {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v} >{l}</option>)}
        </select>
        <span className={`rounded-full border px-3 py-1 text-xs font-bold ${STATUS_CLS[dossier.status] || ""}`}>{dossier.status_label}</span>
        <div className="w-44">
          <DateField testId="dossier-visit" placeholder="Date de visite" value={dossier.visit_date} onChange={(v) => patch({ visit_date: v })} />
        </div>
        <button type="button" data-testid="prepare-quote-btn" disabled={preparing} onClick={prepare} className={`${btnPrimary} ml-auto`}>
          {preparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {preparing ? "L'assistant travaille…" : "Préparer mon devis"}
        </button>
      </div>

      <OfflineBanner onSynced={load} />

      <div className="mt-6 space-y-4">
        <PlateReader dossierId={id} onApplied={load} />

        <VoiceRecorder dossierId={id} onResult={() => load()} />

        {!!dossier.voice_notes?.length && (
          <Section testId="voice-notes" title="Dictées analysées" icon={Sparkles}>
            <div className="mt-3 space-y-3">
              {dossier.voice_notes.slice().reverse().map((vn) => (
                <div key={vn.id} className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
                  <p className="text-sm font-medium text-slate-800">{vn.summary}</p>
                  <p className="mt-2 text-xs text-[#5C6A87] italic">« {vn.transcript} »</p>
                  {!!vn.materials?.length && <p className="mt-2 text-xs text-[#5C6A87]"><strong>Matériel :</strong> {vn.materials.join(", ")}</p>}
                  {!!vn.tasks?.length && <p className="mt-1 text-xs text-[#5C6A87]"><strong>Travaux :</strong> {vn.tasks.join(", ")}</p>}
                  {!!vn.missing?.length && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <p className="text-xs font-bold text-amber-800 inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Informations manquantes</p>
                      <ul className="mt-1.5 space-y-1">
                        {vn.missing.map((m, i) => <li key={i} className="text-xs text-amber-800">• {m}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section testId="dossier-photos" title="Photos du chantier" icon={Camera}>
          <div className="mt-3 flex flex-wrap gap-2">
            {PHOTO_CATEGORIES.map(([v, l]) => (
              <button key={v} type="button" data-testid={`photo-cat-${v}`} onClick={() => setPhotoCategory(v)} className={`rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors ${photoCategory === v ? "border-[#3A5CC8] bg-[#EEF1FC] text-[#3A5CC8]" : "border-[#E2E7F7] text-[#5C6A87]"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="mt-4">
            <PhotoUploader testId="dossier-photo-uploader" photos={[]} onChange={attachPhotos} onOfflineFile={keepPhotoOffline} max={8} hint={`Les photos seront classées dans « ${PHOTO_CATEGORIES.find(([v]) => v === photoCategory)[1]} ».`} />
          </div>
          {!!dossier.photos?.length && (
            <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
              {dossier.photos.map((p) => (
                <div key={p.id} data-testid={`dossier-photo-${p.id}`} className="relative rounded-xl overflow-hidden border border-[#E2E7F7]">
                  <img src={mediaUrl(p.media_id)} alt={p.category} loading="lazy" decoding="async" className="aspect-square w-full object-cover" />
                  <span className="absolute bottom-0 inset-x-0 bg-[#0F2645]/80 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white truncate">{p.category}</span>
                  <button type="button" onClick={() => removeItem("photos", p.id)} className="absolute top-1 right-1 rounded-full bg-white/90 p-1 text-[#5C6A87] hover:text-red-600 transition-colors" aria-label="Supprimer">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section testId="dossier-measures" title="Mesures" icon={Ruler}>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-2">
            <input data-testid="measure-label" className={inputCls} placeholder="Quoi ? (ex. : tubage)" value={measure.label} onChange={(e) => setMeasure({ ...measure, label: e.target.value })} />
            <input data-testid="measure-value" className={inputCls} placeholder="Valeur" value={measure.value} onChange={(e) => setMeasure({ ...measure, value: e.target.value })} />
            <input data-testid="measure-unit" className={inputCls} placeholder="Unité" value={measure.unit} onChange={(e) => setMeasure({ ...measure, unit: e.target.value })} />
            <button type="button" data-testid="measure-add-btn" onClick={addMeasure} className={btnPrimary}>Ajouter</button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {(dossier.measures || []).map((m) => (
              <span key={m.id} data-testid={`measure-${m.id}`} className="inline-flex items-center gap-2 rounded-lg border border-[#E2E7F7] bg-[#F6F8FD] px-3 py-1.5 text-xs text-[#0F2645]">
                <strong>{m.label}</strong> {m.value} {m.unit}
                <button type="button" onClick={() => removeItem("measures", m.id)} className="text-[#93A0BC] hover:text-red-600" aria-label="Supprimer"><Trash2 className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </Section>

        <Section testId="dossier-notes" title="Notes de chantier" icon={StickyNote}>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <textarea data-testid="note-input" rows={2} className={inputCls} placeholder="Ce que vous avez constaté…" value={note} onChange={(e) => setNote(e.target.value)} />
            <button type="button" data-testid="note-add-btn" onClick={addNote} className={btnPrimary}>Ajouter</button>
          </div>
          <div className="mt-3 space-y-2">
            {(dossier.notes || []).slice().reverse().map((n) => (
              <div key={n.id} data-testid={`note-${n.id}`} className="flex items-start gap-3 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] px-4 py-3">
                <p className="text-sm text-[#0F2645] flex-1">{n.text}</p>
                <button type="button" onClick={() => removeItem("notes", n.id)} className="text-[#93A0BC] hover:text-red-600" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </Section>

        <Section testId="dossier-materials" title="Matériel à acheter" icon={ShoppingCart}>
          <p className="mt-1 text-xs text-[#5C6A87]">
            Notez sur place ce qu'il faut commander. Les matériels cités dans vos dictées s'ajoutent automatiquement.
          </p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-2">
            <input data-testid="material-label" className={inputCls} placeholder="Matériel (ex. : kit raccordement)" value={material.label} onChange={(e) => setMaterial({ ...material, label: e.target.value })} />
            <input data-testid="material-quantity" className={inputCls} placeholder="Qté" value={material.quantity} onChange={(e) => setMaterial({ ...material, quantity: e.target.value })} />
            <input data-testid="material-unit" className={inputCls} placeholder="Unité" value={material.unit} onChange={(e) => setMaterial({ ...material, unit: e.target.value })} />
            <input data-testid="material-supplier" className={inputCls} placeholder="Fournisseur" value={material.supplier} onChange={(e) => setMaterial({ ...material, supplier: e.target.value })} />
            <button type="button" data-testid="material-add-btn" onClick={addMaterial} className={btnPrimary}>Ajouter</button>
          </div>

          {!!(dossier.materials || []).length && (
            <>
              <div className="mt-4 space-y-2">
                {dossier.materials.map((m) => (
                  <div key={m.id} data-testid={`material-${m.id}`} className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 transition-colors duration-200 ${m.bought ? "border-green-200 bg-green-50" : "border-[#E2E7F7] bg-white"}`}>
                    <button type="button" data-testid={`material-toggle-${m.id}`} onClick={() => toggleMaterial(m)} aria-label="Marquer comme acheté">
                      <CheckCircle2 className={`w-4 h-4 ${m.bought ? "text-green-600" : "text-slate-300"}`} />
                    </button>
                    <span className={`text-sm font-medium ${m.bought ? "text-green-800 line-through" : "text-slate-800"}`}>{m.label}</span>
                    <span className="text-xs text-[#5C6A87]">{m.quantity} {m.unit}</span>
                    {m.supplier && <span className="text-xs text-[#93A0BC]">{m.supplier}</span>}
                    {m.note && <span className="text-[11px] text-[#93A0BC] italic">{m.note}</span>}
                    <button type="button" data-testid={`material-delete-${m.id}`} onClick={() => removeItem("materials", m.id)} className="ml-auto text-[#93A0BC] hover:text-red-600 transition-colors" aria-label="Supprimer">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <p className="text-xs font-semibold text-[#5C6A87]">
                  {dossier.materials.filter((m) => !m.bought).length} article(s) à acheter sur {dossier.materials.length}
                </p>
                <button
                  type="button"
                  data-testid="material-copy-btn"
                  onClick={() => copyToClipboard(
                    `Liste matériel — ${dossier.reference} · ${dossier.client_name}\n` +
                    dossier.materials.filter((m) => !m.bought).map((m) => `- ${m.label} × ${m.quantity} ${m.unit}${m.supplier ? ` (${m.supplier})` : ""}`).join("\n"),
                    "Liste de matériel copiée.",
                  )}
                  className={btnGhost}
                >
                  <Copy className="w-3.5 h-3.5" /> Copier la liste
                </button>
              </div>
            </>
          )}
        </Section>

        <Section testId="dossier-checklist" title="Checklist métier" icon={ListChecks}>
          <button type="button" data-testid="checklist-generate-btn" onClick={generateChecklist} className={`${btnGhost} mt-3`}>
            Générer la checklist {dossier.job_type_label?.toLowerCase()}
          </button>
          <div className="mt-3 space-y-2">
            {(dossier.checklist || []).map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`check-${item.id}`}
                onClick={() => toggleCheck(item)}
                className={`w-full flex items-center gap-3 rounded-xl border px-4 py-2.5 text-left transition-colors duration-200 ${item.done ? "border-green-200 bg-green-50" : "border-[#E2E7F7] bg-white hover:border-[#E2E7F7]"}`}
              >
                <CheckCircle2 className={`w-4 h-4 shrink-0 ${item.done ? "text-green-600" : "text-slate-300"}`} />
                <span className={`text-sm ${item.done ? "text-green-800 line-through" : "text-[#0F2645]"}`}>{item.label}</span>
              </button>
            ))}
          </div>
        </Section>

        <Section testId="dossier-trade-forms" title="Fiche technique métier" icon={Wrench}>
          <p className="mt-1 text-xs text-[#5C6A87]">Complétez seulement ce qui est utile — l'assistant utilise ces informations pour le devis.</p>
          <div className="mt-3 space-y-2">
            {forms.map((section) => (
              <div key={section.key} className="rounded-xl border border-[#E2E7F7] overflow-hidden">
                <button type="button" data-testid={`form-section-${section.key}`} onClick={() => setOpenForm(openForm === section.key ? null : section.key)} className="w-full flex items-center justify-between gap-3 bg-[#F6F8FD] px-4 py-3 text-left">
                  <span className="text-sm font-semibold text-slate-800">{section.title}</span>
                  {openForm === section.key ? <ChevronUp className="w-4 h-4 text-[#93A0BC]" /> : <ChevronDown className="w-4 h-4 text-[#93A0BC]" />}
                </button>
                {openForm === section.key && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
                    {section.fields.map((field) => (
                      <label key={field.label} className="block">
                        <span className="block text-[11px] font-semibold uppercase tracking-wide text-[#5C6A87] mb-1">{field.label}</span>
                        {field.type === "select" ? (
                          <select
                            data-testid={`field-${field.label}`}
                            className={inputCls}
                            value={structured[field.label] || ""}
                            onChange={(e) => patch({ structured: { ...structured, [field.label]: e.target.value } })}
                          >
                            <option value="">—</option>
                            {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : (
                          <input
                            data-testid={`field-${field.label}`}
                            className={inputCls}
                            defaultValue={structured[field.label] || ""}
                            onBlur={(e) => e.target.value !== (structured[field.label] || "") && patch({ structured: { ...structured, [field.label]: e.target.value } })}
                          />
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>

        <Section
          testId="dossier-interventions"
          title="Interventions"
          icon={HardHat}
          action={
            <button
              type="button"
              data-testid="create-intervention-btn"
              onClick={() => createIntervention(dossier.quotes?.[0]?.id || "")}
              className={btnGhost}
            >
              <Plus className="w-3.5 h-3.5" /> Créer l'intervention
            </button>
          }
        >
          <p className="mt-1 text-xs text-[#5C6A87]">
            L'intervention reprend le dossier et les lignes du devis, puis génère le rapport à remettre au client.
          </p>
          <div className="mt-3 space-y-2">
            {interventions.map((iv) => (
              <Link
                key={iv.id}
                to={`/studio/interventions/${iv.id}`}
                data-testid={`intervention-${iv.reference}`}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E2E7F7] px-4 py-3 hover:border-[#3A5CC8]/40 transition-colors"
              >
                <span className="text-sm font-bold text-[#0F2645]">{iv.reference}</span>
                <span className="text-xs text-[#5C6A87]">{beDate(iv.date)}</span>
                {iv.signature && <span className="text-xs font-semibold text-green-700">Signé</span>}
                {iv.sent_at && <span className="text-xs text-[#93A0BC]">Envoyé le {beDate(iv.sent_at)}</span>}
              </Link>
            ))}
            {!interventions.length && <p className="text-sm text-[#5C6A87]">Aucune intervention pour ce chantier.</p>}
          </div>
        </Section>

        <Section testId="dossier-visit-plan" title="Planifier la visite technique" icon={CalendarClock}>
          <p className="mt-1 text-xs text-[#5C6A87]">
            Le client reçoit un email « Visite technique – Devis » avec le jour, l'heure d'arrivée, l'adresse et le motif.
          </p>
          {dossier.visit_planned_at && (
            <p data-testid="visit-planned-at" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-1.5 text-xs font-semibold text-green-700">
              <MailCheck className="w-3.5 h-3.5" />
              Visite envoyée au client · {beDate(dossier.visit_date)}
              {dossier.visit_time_start ? ` entre ${dossier.visit_time_start} et ${dossier.visit_time_end || "…"}` : ""}
              {dossier.visit_free_quote ? " · Devis gratuit – 0 €" : ""}
              {dossier.visit_reminder_sent_at ? " · rappel envoyé" : (dossier.visit_reminder === false ? " · sans rappel" : " · rappel prévu la veille")}
            </p>
          )}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="col-span-2 sm:col-span-2">
              <DateField
                testId="visit-plan-date"
                placeholder="Jour du rendez-vous"
                value={visitPlan?.date ?? dossier.visit_date ?? ""}
                onChange={(v) => setVisitPlan((p) => ({ ...(p || {}), date: v }))}
              />
            </div>
            <input
              data-testid="visit-plan-start"
              type="time"
              className={inputCls}
              value={visitPlan?.time_start ?? dossier.visit_time_start ?? ""}
              onChange={(e) => setVisitPlan((p) => ({ ...(p || {}), time_start: e.target.value }))}
            />
            <input
              data-testid="visit-plan-end"
              type="time"
              className={inputCls}
              value={visitPlan?.time_end ?? dossier.visit_time_end ?? ""}
              onChange={(e) => setVisitPlan((p) => ({ ...(p || {}), time_end: e.target.value }))}
            />
          </div>
          <input
            data-testid="visit-plan-note"
            className={`${inputCls} mt-2`}
            placeholder="Information au client (ex. : merci de prévoir l'accès à la cave)"
            value={visitPlan?.note ?? dossier.visit_note ?? ""}
            onChange={(e) => setVisitPlan((p) => ({ ...(p || {}), note: e.target.value }))}
          />
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-[#0F2645]">
              <input
                data-testid="visit-plan-free"
                type="checkbox"
                className="w-4 h-4 accent-[#3A5CC8]"
                checked={visitPlan?.free_quote ?? dossier.visit_free_quote ?? false}
                onChange={(e) => setVisitPlan((p) => ({ ...(p || {}), free_quote: e.target.checked }))}
              />
              Afficher « Devis gratuit – 0 € » dans l'email
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-[#0F2645]">
              <input
                data-testid="visit-plan-reminder"
                type="checkbox"
                className="w-4 h-4 accent-[#3A5CC8]"
                checked={visitPlan?.reminder ?? dossier.visit_reminder ?? true}
                onChange={(e) => setVisitPlan((p) => ({ ...(p || {}), reminder: e.target.checked }))}
              />
              Rappel automatique la veille
            </label>
            <button
              type="button"
              data-testid="visit-plan-send"
              disabled={visitBusy}
              onClick={() => planVisit()}
              className={btnPrimary}
            >
              {visitBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer la planification
            </button>
            {dossier.visit_planned_at && (
              <button
                type="button"
                data-testid="visit-reminder-now"
                disabled={visitBusy}
                onClick={async () => {
                  setVisitBusy(true);
                  try {
                    await studio.post(`/dossiers/${id}/visit-reminder`);
                    toast.success("Rappel envoyé au client.");
                    load();
                  } catch (e) {
                    toast.error(apiError(e, "Envoi impossible."));
                  } finally {
                    setVisitBusy(false);
                  }
                }}
                className={btnGhost}
              >
                <MailCheck className="w-3.5 h-3.5" /> Envoyer le rappel maintenant
              </button>
            )}
          </div>
        </Section>

        <Section testId="dossier-report" title="Rapport de visite" icon={ClipboardCheck}>
          <p className="mt-1 text-xs text-[#5C6A87]">
            Un document clair à laisser au client à la fin de la visite : constats, mesures, matériel prévu et photos. Aucun prix, aucun engagement.
          </p>
          <textarea
            data-testid="dossier-summary"
            rows={3}
            className={`${inputCls} mt-3`}
            placeholder="Résumé pour le client (ex. : chaudière de 2004 en fin de vie, tubage à remplacer…)"
            defaultValue={dossier.summary || ""}
            onBlur={(e) => e.target.value !== (dossier.summary || "") && patch({ summary: e.target.value })}
          />
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button type="button" data-testid="report-pdf-btn" disabled={!!reportBusy} onClick={downloadReport} className={btnGhost}>
              {reportBusy === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Télécharger le PDF
            </button>
            <button type="button" data-testid="report-send-btn" disabled={!!reportBusy} onClick={sendReport} className={btnPrimary}>
              {reportBusy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer au client
            </button>
            {dossier.report_sent_at && (
              <span data-testid="report-sent-at" className="text-xs font-semibold text-green-700">
                Envoyé le {beDate(dossier.report_sent_at.slice(0, 10))}
              </span>
            )}
          </div>
        </Section>

        {!!dossier.quotes?.length && (
          <Section testId="dossier-quotes" title="Devis du dossier" icon={FileText}>
            <div className="mt-3 space-y-2">
              {dossier.quotes.map((q) => (
                <div key={q.id} data-testid={`dossier-quote-${q.id}`} className="flex items-stretch overflow-hidden rounded-xl border border-[#E2E7F7] bg-white hover:border-[#3A5CC8] transition-colors">
                  <button type="button" data-testid={`dossier-quote-open-${q.id}`} onClick={() => navigate(`/studio/devis/${q.id}`)} className="flex min-w-0 flex-1 flex-wrap items-center gap-3 px-4 py-3 text-left">
                    <span className="text-sm font-bold text-slate-800">{q.number}</span>
                    <span className="text-xs text-[#5C6A87]">{beDate(q.issue_date)}</span>
                    <StatusBadge value={q.status} />
                    {q.status === "draft" && <span className="text-xs font-semibold text-[#3A5CC8]">Reprendre et valider →</span>}
                    <span className="ml-auto text-sm font-bold text-[#0F2645]">{eur(q.total)}</span>
                  </button>
                  {["draft", "refused", "expired", "cancelled"].includes(q.status) && (
                    <button type="button" data-testid={`dossier-quote-delete-${q.id}`} onClick={() => removeQuote(q)} aria-label="Supprimer le devis" title="Supprimer ce brouillon" className="flex w-12 shrink-0 items-center justify-center border-l border-[#EEF1FC] text-[#93A0BC] transition-colors hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </StudioLayout>
  );
}
